// FeedSorterTabs.jsx
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { LIVESTREAM_FEED_SORTINGS_V2 } from '../RemoteConfigKeys.js';

import { EMPTY_ARRAY, NULL_FUNCTION } from '../resource/defaults.js';
import { ButtonId } from '../resource/trackingConstants.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { isDefaultSorting } from '../resource/feedUtils.js';

import media, { hover } from '../style/media.js';
import { color } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';

const FeedSorterTabs = ({
  baseFeedName = '',
  sortOptions = EMPTY_ARRAY,
  selectedOptions = EMPTY_ARRAY,
  setSelectedOption = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();

  const hasSortingOptions = selectedOptions.some(selectedOption =>
    selectedOption.startsWith(LIVESTREAM_FEED_SORTINGS_V2)
  );

  return (
    <StyledFeedSorterTabs>
      {sortOptions.map(option => {
        const groupOption = `${LIVESTREAM_FEED_SORTINGS_V2}/${option}`;
        const isDefaultOption =
          !hasSortingOptions && isDefaultSorting({ baseFeedName, option });
        const isActive =
          selectedOptions.includes(groupOption) || isDefaultOption;

        return (
          <Tab key={option} active={isActive}>
            <Button
              data-element_id={ButtonId.All.ButtonSorting}
              data-element_group={LIVESTREAM_FEED_SORTINGS_V2}
              data-element_option={option}
              data-tracking_payload={{
                'filter.group': LIVESTREAM_FEED_SORTINGS_V2,
                'filter.option': option,
                'filter.selectedOptions': selectedOptions.join(','),
              }}
              onClick={() =>
                setSelectedOption({
                  groupOption: `${LIVESTREAM_FEED_SORTINGS_V2}/${option}`,
                  isSingleSelection: true,
                })
              }
            >
              <TextWrapper>
                <Text>
                  {t(
                    getTranslationKey({
                      key: option,
                    }),
                    {
                      ns: TranslationNamespace.FEED,
                    }
                  )}
                </Text>
              </TextWrapper>
            </Button>
          </Tab>
        );
      })}
    </StyledFeedSorterTabs>
  );
};

FeedSorterTabs.propTypes = {
  baseFeedName: PropTypes.string,
  sortOptions: PropTypes.array,
  selectedOptions: PropTypes.array,
  setSelectedOption: PropTypes.func,
};

const StyledFeedSorterTabs = styled.div`
  display: none;
  align-items: space-between;
  justify-content: center;
  height: auto;
  padding: 0 16px;
  border-bottom: 1px solid ${color.neutral['20_alpha_40']};
  box-sizing: content-box;

  ${media.mobile`
    display: flex;
  `}
`;

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: fit-content;
  max-height: 50px;
  padding: 8px 12px;
  border-radius: 40px;
  transition: 0.3s;
`;

const Text = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  width: fit-content;
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${color.white};
  text-align: center;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Button = styled.button`
  ${ResetButtonStyle};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 100%;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 12px;
    width: calc(100% - 24px);
    height: 4px;
    background-color: ${color.tealBlue};
    opacity: 0;
    transition: 0.3s;
  }

  ${hover`
    ${TextWrapper} {
      background-color: ${color.neutral['20_alpha_80']};
    }
  `}
`;

const Tab = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: auto;
  min-height: 40px;

  ${({ active }) =>
    active &&
    `
    ${Button} {
      pointer-events: none;
      cursor: default;
      
      &::after {
        opacity: 1;
      }
    }
  `}
`;

export default FeedSorterTabs;
